import React, { useState, useEffect } from 'react';

interface StudyTip {
  id: number;
  icon: string;
  title: string;
  tip: string;
  category: 'motivation' | 'technique' | 'ai' | 'performance';
  color: string;
}

const AIStudyTips: React.FC = () => {
  const [currentTip, setCurrentTip] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const studyTips: StudyTip[] = [
    {
      id: 1,
      icon: 'fas fa-brain',
      title: 'AI-Powered Learning',
      tip: 'Our AI will analyze your mistakes and create personalized question sets to target your weak areas!',
      category: 'ai',
      color: '#667eea'
    },
    {
      id: 2,
      icon: 'fas fa-target',
      title: 'Smart Focus Mode',
      tip: 'Take breaks every 25 minutes! The upcoming AI will track your focus patterns and suggest optimal study intervals.',
      category: 'technique',
      color: '#28a745'
    },
    {
      id: 3,
      icon: 'fas fa-chart-line',
      title: 'Progress Tracking',
      tip: 'Consistency beats intensity! Our AI will show you exactly how much you\'re improving each day.',
      category: 'performance',
      color: '#ffc107'
    },
    {
      id: 4,
      icon: 'fas fa-lightbulb',
      title: 'Smart Insights',
      tip: 'The AI will identify patterns in your learning and suggest the best times for you to study different subjects!',
      category: 'ai',
      color: '#17a2b8'
    },
    {
      id: 5,
      icon: 'fas fa-star',
      title: 'Motivation Boost',
      tip: 'Every small step counts! The AI will celebrate your achievements and keep you motivated on tough days.',
      category: 'motivation',
      color: '#dc3545'
    },
    {
      id: 6,
      icon: 'fas fa-puzzle-piece',
      title: 'Adaptive Questions',
      tip: 'No more wasting time on concepts you\'ve mastered! AI will focus on what you need to learn next.',
      category: 'ai',
      color: '#6f42c1'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      
      setTimeout(() => {
        setCurrentTip((prev) => (prev + 1) % studyTips.length);
        setIsAnimating(false);
      }, 300);
    }, 4000);

    return () => clearInterval(interval);
  }, [studyTips.length]);

  const nextTip = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentTip((prev) => (prev + 1) % studyTips.length);
      setIsAnimating(false);
    }, 300);
  };

  const prevTip = () => {
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentTip((prev) => (prev - 1 + studyTips.length) % studyTips.length);
      setIsAnimating(false);
    }, 300);
  };

  const getCategoryBadge = (category: string) => {
    const badges = {
      ai: { text: 'AI Feature', class: 'bg-primary' },
      technique: { text: 'Study Tip', class: 'bg-success' },
      performance: { text: 'Performance', class: 'bg-warning' },
      motivation: { text: 'Motivation', class: 'bg-danger' }
    };
    return badges[category as keyof typeof badges];
  };

  return (
    <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '20px' }}>
      <div className="card-header bg-transparent border-0 p-4">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="fw-bold mb-0">
            <i className="fas fa-robot me-2 text-primary"></i>
            AI Study Assistant
          </h5>
          <div className="d-flex gap-2">
            <button 
              className="btn btn-sm btn-outline-primary rounded-circle"
              onClick={prevTip}
              style={{ width: '35px', height: '35px' }}
            >
              <i className="fas fa-chevron-left"></i>
            </button>
            <button 
              className="btn btn-sm btn-outline-primary rounded-circle"
              onClick={nextTip}
              style={{ width: '35px', height: '35px' }}
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
      
      <div className="card-body p-4 pt-0">
        <div 
          className={`transition-all ${isAnimating ? 'opacity-0 transform-scale-95' : 'opacity-100 transform-scale-100'}`}
          style={{ 
            transition: 'all 0.3s ease',
            transform: isAnimating ? 'scale(0.95)' : 'scale(1)'
          }}
        >
          <div className="row align-items-center">
            <div className="col-auto">
              <div 
                className="rounded-circle d-flex align-items-center justify-content-center"
                style={{ 
                  width: '60px', 
                  height: '60px',
                  backgroundColor: studyTips[currentTip].color + '20',
                  border: `3px solid ${studyTips[currentTip].color}30`
                }}
              >
                <i 
                  className={`${studyTips[currentTip].icon} fa-lg`}
                  style={{ color: studyTips[currentTip].color }}
                ></i>
              </div>
            </div>
            
            <div className="col">
              <div className="d-flex align-items-center mb-2">
                <h6 className="fw-bold mb-0 me-2">{studyTips[currentTip].title}</h6>
                <span 
                  className={`badge rounded-pill ${getCategoryBadge(studyTips[currentTip].category).class} text-white`}
                  style={{ fontSize: '10px' }}
                >
                  {getCategoryBadge(studyTips[currentTip].category).text}
                </span>
              </div>
              <p className="text-muted mb-0">{studyTips[currentTip].tip}</p>
            </div>
          </div>
        </div>
        
        {/* Progress Indicators */}
        <div className="d-flex justify-content-center mt-4 gap-2">
          {studyTips.map((_, index) => (
            <button
              key={index}
              className={`border-0 rounded-pill ${
                index === currentTip ? 'bg-primary' : 'bg-light'
              }`}
              style={{ 
                width: '8px', 
                height: '8px',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onClick={() => setCurrentTip(index)}
            ></button>
          ))}
        </div>
        
        {/* AI Assistant Note */}
        <div className="mt-3 p-3 rounded-3 d-flex align-items-center" style={{ backgroundColor: '#f8f9ff' }}>
          <div className="me-2">
            <div className="spinner-grow spinner-grow-sm text-primary" role="status" style={{ width: '12px', height: '12px' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
          <small className="text-muted">
            <strong>AI Tip:</strong> These insights will be personalized based on your actual performance once mock tests are live!
          </small>
        </div>
      </div>
    </div>
  );
};

export default AIStudyTips;